import { errorText } from '@/api/client';
import {
  type ApiAbsenceType,
  type ApiPayrollType,
  type ApiRateRule,
  type ApiShiftType,
  type RateRuleWrite,
  type TenantWrite,
  useAbsenceTypeWrites,
  useAbsenceTypes,
  usePayrollTypes,
  usePayrollWrites,
  useRateRules,
  useShiftTypeWrites,
  useShiftTypes,
  useTeamWrites,
  useTeams,
  useTenantWrite,
  useUsers,
  useWorkspaceWrite,
} from '@/api/data';
import { ratesTo, useLabour, useSaveLabour } from '@/api/labour';
import { T, useI18n } from '@/i18n';
import { PowerOfficeRates } from '@/pages/lonn/PowerOfficeRates';
import { POWEROFFICE, TRIPLETEX, chosen, systemOf } from '@/pages/lonn/payrollSystem';
import { typeTime } from '@/rules';
import { breakChoiceOf, breakExample } from '@/rules/breakSetting';
import { costOf, ratesOf } from '@/rules/labour';
import { placeWarning } from '@/rules/place';
import { helpUrl } from '@/shell/HelpButton';
import { LocalFiguresOffer } from '@/shell/LocalFiguresOffer';
import { useWelcome } from '@/shell/Welcome';
import { useDirty } from '@/shell/context';
import { HOURS, type NeedProfile } from '@/shell/local';
import { clearLegacyModeOverride, legacyModeOverride, useTenant } from '@/shell/tenant';
import { Button, Chip, Drawer, Empty, Panel, Switch, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { DN, DNL, num, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { PageHead, TabPanel, Tabs } from '@/ui/page';
import type { BreakMode } from '@timefly/core';
import { Suspense, lazy, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  A_MELDING,
  PAYER,
  balanceText,
  lawNote,
  limitsText,
  ownVacationWithoutDeduction,
  payerText,
  unitText,
} from './fravaer/catalogue';
import { CATEGORIES } from './fravaer/category';
import AiHistorikk from './innstillinger/AiHistorikk';
import Apningstider from './innstillinger/Apningstider';
import Endringer from './innstillinger/Endringer';
import { FerdigheterTab } from './innstillinger/Ferdigheter';
import { FollowUp } from './innstillinger/FollowUp';
import Oppgaver from './innstillinger/Oppgaver';
import ps from './innstillinger/Pause.module.css';
import Regelprofil from './innstillinger/Regelprofil';
import Roller from './innstillinger/Roller';
import Varsler from './innstillinger/Varsler';
import { GROUPS, GroupSummary, SECTIONS, SectionList, groupOf } from './innstillinger/groups';
import { overtimeBelowLaw } from './innstillinger/overtime';
import { PowerOfficeConnect } from './lonn/PowerOffice';
import { PowerOfficeFileCheck } from './lonn/PowerOfficeFileCheck';
/**
 * Innstillinger: the nine tenant controls that exist (PUT /tenant/:id), shift
 * types, absence types and teams (their admin routes), payroll types and rate
 * rules read only, and the role matrix drawn read only until plan 0006. The
 * rest of the prototype's settings are listed as waiting for plan 0003.
 * Since the manager redesign (2026-10-09) the twelve sections sit in four groups, each with a
 * summary of its real values and the sections as rows that open their editor in place
 * (innstillinger/groups.tsx); /innstillinger/<section> still opens that section.
 */
import { SettingsOverview } from './lonn/SettingsOverview';
import { SetupDraftPanel } from './lonn/SetupDraft';
import { TripletexConnect } from './lonn/Tripletex';
// Leaflet and its tiles load only when Stempling is opened
const Kartvelger = lazy(() => import('./innstillinger/Kartvelger'));

/** the history sections only the company's Administrator reads (the server refuses anyone else) */
const ADMIN_READ = ['ai', 'endringer'];
/** the tabs whose writes need the company's Administrator on the server */
const ADMIN_TABS = ['modus', 'prosjekter', 'lonn', 'roller', 'regelprofil'];

function Setting({
  label,
  kind,
  children,
  note,
  error,
}: {
  label: string;
  kind: 'co' | 'std' | 'soon';
  children: React.ReactNode;
  note?: string;
  /** a refused or failed save of this setting, beside it (#267 SET-28) */
  error?: string | undefined;
}) {
  return (
    <div className="set">
      <div>
        {label}
        <br />
        <Chip tone={kind === 'co' ? 'neu' : kind === 'std' ? 'act' : 'warn'} small>
          {kind === 'co' ? T('Bedrift') : kind === 'std' ? T('Standard') : T('Ikke tilgjengelig ennå')}
        </Chip>
      </div>
      <div className="val">
        {children}
        {error && (
          <span role="alert" className="set-err">
            {error}
          </span>
        )}
        {note && <em>{note}</em>}
      </div>
    </div>
  );
}

export default function Innstillinger() {
  const { tab: asked = 'bedrift' } = useParams();
  // a group with one section opens that section; /innstillinger/lonnint was an empty page (#274 N11)
  const tab = GROUPS.find((g) => g.key === asked && g.sections.length === 1)?.sections[0] ?? asked;
  const go = useNavigate();
  // /innstillinger/lonn#integrasjon from search lands on the section, once the page has drawn it (#272 SS-04)
  // keyed on the navigation too: the same #hash asked again (from search) scrolls again (#272 SS-23)
  const { hash, key } = useLocation();
  useEffect(() => {
    if (!hash || !key) return;
    const t = setTimeout(
      () => document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' }),
      300,
    );
    return () => clearTimeout(t);
  }, [hash, key]);
  const { tenant, mode, workspace, me } = useTenant();
  const ww = useWorkspaceWrite();
  const tw = useTenantWrite();
  const { locale, setLocale } = useI18n();
  const [typeDraft, setTypeDraft] = useState<(Partial<ApiShiftType> & { from: string; to: string }) | null>(
    null,
  );
  const [absDraft, setAbsDraft] = useState<Partial<ApiAbsenceType> | null>(null);
  const [tick, setTick] = useState(0);
  // the company's figures, on the server since 2026-09-25 (api/labour.ts)
  const labour = useLabour();
  const saveLabour = useSaveLabour();
  const need: NeedProfile = labour.need;
  const saveNeed = (next: NeedProfile, done?: string) =>
    saveLabour.mutate(
      { revision: labour.revision, staffing_need: next },
      { onSuccess: () => done && toast(done), onError: (e) => toast(errorText(e)) },
    );
  const typesQ = useShiftTypes();
  const absTypesQ = useAbsenceTypes();
  const teamsQ = useTeams();
  const usersQ = useUsers();
  const ptQ = usePayrollTypes();
  const rrQ = useRateRules();
  const stw = useShiftTypeWrites();
  const atw = useAbsenceTypeWrites();
  const tmw = useTeamWrites();
  const pw = usePayrollWrites();
  const [ruleDraft, setRuleDraft] = useState<(Partial<RateRuleWrite> & { id?: number }) | null>(null);
  const [typeEdit, setTypeEdit] = useState<(Partial<ApiPayrollType> & { id?: number }) | null>(null);

  const [fieldError, setFieldError] = useState<Partial<Record<keyof TenantWrite, string>>>({});
  // #267 SET-28, SET-30: the save says which value it replaces, so the server refuses (409) a field another
  // session changed meanwhile instead of overwriting it; a failure is told beside the field as well as in
  // the toast, and the typed value stays in the field (the input is keyed on the stored value)
  const save = async (body: TenantWrite) => {
    if (!tenant) return;
    const keys = Object.keys(body) as (keyof TenantWrite)[];
    const expected = Object.fromEntries(
      keys.filter((k) => k in tenant).map((k) => [k, tenant[k as keyof typeof tenant] ?? null]),
    );
    const mark = (msg: string | undefined) =>
      setFieldError((f) => ({ ...f, ...Object.fromEntries(keys.map((k) => [k, msg])) }));
    try {
      await tw.mutateAsync({ id: tenant.id, body: { ...body, expected } });
      mark(undefined);
      toast(T('Lagret'));
    } catch (e) {
      mark(errorText(e));
      toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`);
    }
  };
  // the modus panel names its own save for the workspace; this one writes the company row
  const saveTenant = save;
  useDirty('settings-drafts', !!(typeDraft || absDraft || ruleDraft || typeEdit), 'Innstillinger');
  // check: a message refuses the value; nothing is sent and the stored value stays (TF-TIME-TOTALS item 4)
  const numIn = (
    k: keyof TenantWrite,
    label: string,
    width = 90,
    step = '1',
    check?: (v: number | null) => string | null,
    write: (v: number | null) => TenantWrite = (v) => ({ [k]: v }) as TenantWrite,
  ) => (
    <input
      className="inp"
      type="number"
      step={step}
      style={{ width }}
      aria-label={label}
      aria-invalid={!!fieldError[k]}
      key={`${k}-${String(tenant?.[k as keyof typeof tenant])}`}
      defaultValue={(tenant?.[k as keyof typeof tenant] as number | null | undefined) ?? ''}
      onBlur={(e) => {
        const v = e.target.value === '' ? null : Number(e.target.value);
        const err = check ? check(v) : null;
        setFieldError((f) => ({ ...f, [k]: err ?? undefined }));
        if (err) return;
        if (v !== (tenant?.[k as keyof typeof tenant] ?? null)) save(write(v));
      }}
    />
  );
  const positiveBreak = (v: number | null) =>
    v == null || !(v >= 0) ? T('Oppgi en pauselengde, 0 eller mer.') : null;
  const sw = (on: boolean, yes: string, no: string, onClick: () => void) => (
    <Switch checked={on} onChange={onClick} label={on ? yes : no} />
  );

  let body: React.ReactNode = null;
  if (tab === 'bedrift')
    body = (
      <Panel>
        <Setting label={T('Navn')} kind="co" error={fieldError.name}>
          <input
            className="inp w"
            aria-label={T('Navn')}
            key={tenant?.name}
            defaultValue={tenant?.name ?? ''}
            onBlur={(e) =>
              e.target.value && e.target.value !== tenant?.name && save({ name: e.target.value })
            }
          />
        </Setting>
        {/* plan 0027 batch C: the number, and what the Enhetsregisteret says for it */}
        <Setting
          label={T('Organisasjonsnummer')}
          kind="co"
          error={fieldError.org_number}
          note={
            tenant?.org_number
              ? tenant.brreg_name
                ? `${tenant.brreg_name}${tenant.nace_text ? `, ${tenant.nace_code} ${tenant.nace_text}` : ''} ${T('(Enhetsregisteret)')}`
                : T('Enhetsregisteret svarte ikke; nummeret er lagret.')
              : T('Ni siffer. Navn og næringskode hentes fra Enhetsregisteret.')
          }
        >
          <input
            className="inp"
            style={{ width: 140 }}
            inputMode="numeric"
            aria-label={T('Organisasjonsnummer')}
            key={`org-${tenant?.org_number ?? ''}`}
            defaultValue={tenant?.org_number ?? ''}
            onBlur={(e) => {
              const v = e.target.value.replace(/\s/g, '') || null;
              if (v !== (tenant?.org_number ?? null)) save({ org_number: v });
            }}
          />
        </Setting>
        <Setting
          label={T('Tariffavtale')}
          kind="co"
          note={T(
            'Bedriften svarer selv for at dette stemmer; Timefly kan ikke se det i noe register. Med tariffavtale kan gjennomsnittsberegning avtales med tillitsvalgte (arbeidsmiljøloven § 10-5 (2)).',
          )}
        >
          {sw(!!tenant?.tariff_bound, T('Bundet av tariffavtale'), T('Ikke bundet av tariffavtale'), () =>
            save({ tariff_bound: !tenant?.tariff_bound }),
          )}
        </Setting>
        <Setting
          label={T('Språk på ledersiden')}
          kind="std"
          note={T('Gjelder bare deg, på denne maskinen. De ansatte velger sitt eget språk.')}
        >
          <div className="seg">
            <button type="button" aria-pressed={locale === 'nb'} onClick={() => setLocale('nb')}>
              {T('Norsk bokmål')}
            </button>
            <button type="button" aria-pressed={locale === 'en'} onClick={() => setLocale('en')}>
              English
            </button>
          </div>
        </Setting>
        <Setting label={T('Abonnement')} kind="co">
          <span>{(tenant as { subscription_type?: string } | undefined)?.subscription_type ?? '–'}</span>
        </Setting>
      </Panel>
    );
  if (tab === 'arbeidstid')
    body = (
      <Panel>
        <Setting
          label={T('Avtalt daglig arbeidstid')}
          kind="std"
          error={fieldError.shift_length_hours}
          note={T('Dagsregelen «over avtalt» måler mot dette.')}
        >
          {numIn('shift_length_hours', T('Avtalt daglig arbeidstid, timer'), 90, '0.5')} {T('t per dag')}
        </Setting>
        {/* plan 0027 batch C: the fixed 52-week period for the 200 h overtime limit */}
        <Setting
          label={T('Start på 52-ukersperioden')}
          kind="co"
          error={fieldError.overtime_period_start}
          note={T(
            'Overtid kan være høyst 200 timer i løpet av 52 uker (arbeidsmiljøloven § 10-6 (6)). Med en startdato telles hver periode på 52 uker fra den; tom telles de siste 52 ukene.',
          )}
        >
          <DateInput
            className="inp"
            aria-label={T('Start på 52-ukersperioden')}
            key={`otstart-${tenant?.overtime_period_start ?? ''}`}
            defaultValue={tenant?.overtime_period_start ?? ''}
            onCommit={(value) => {
              const v = value || null;
              if (v !== (tenant?.overtime_period_start ?? null)) save({ overtime_period_start: v });
            }}
          />
        </Setting>
        {/* plan 0027 B: the company's full-time week, which a person without their own follows */}
        <Setting
          label={T('Avtalt ukentlig arbeidstid')}
          kind="std"
          error={fieldError.agreed_week_hours}
          note={T(
            'Heltid i bedriften, høyst 40 timer (arbeidsmiljøloven § 10-4). Ansatte uten egen uke følger denne; står den tom, regnes 37,5 t. Deltid settes på ansattkortet.',
          )}
        >
          {numIn('agreed_week_hours', T('Avtalt ukentlig arbeidstid, timer'), 90, '0.01', (v) =>
            v == null || (v > 0 && v <= 40) ? null : T('Et tall over 0 og høyst 40'),
          )}{' '}
          {T('t per uke')}
        </Setting>
        <Setting
          label={T('Utvidede overtidsgrenser (tariff)')}
          kind="soon"
          note={T(
            'Kommer. For bedrifter med Tariffavtale slått på under Bedrift: overtidsgrensene 20/50/300 timer i stedet for 10/25/200. Kontrollen i vaktplanen bruker 10 og 20 timer fast i dag.',
          )}
        >
          <span style={{ color: 'var(--ink3)' }}>{T('Nei, fast')}</span>
        </Setting>
        <Setting
          label={T('Åpningstider')}
          kind="std"
          note={T(
            'Når bedriften har åpent, per ukedag. Appen bruker dette til å vise når en ansatt kan si seg tilgjengelig, og gråer ut timene dere har stengt.',
          )}
        >
          <Apningstider hours={tenant?.opening_hours} onSave={(opening_hours) => save({ opening_hours })} />
        </Setting>
        {/* #294 ONB-09: the company's punch tolerance; it flags a deviation and never moves a punch */}
        <Setting
          label={T('Toleranse for stempling')}
          kind="co"
          error={fieldError.punch_tolerance_minutes}
          note={T(
            'Hvor mange minutter en inn- eller utstempling kan avvike fra vakten før Kontroll viser den som avvik, og før noen regnes som ikke stemplet inn. Samme grense gjelder for tidlig og sent. 0 viser ethvert avvik på ett minutt eller mer. Stemplingen blir ikke avrundet, og timer og lønn regnes av faktisk tid som før. Arbeidstidsreglene endres ikke.',
          )}
        >
          {numIn('punch_tolerance_minutes', T('Toleranse for stempling, minutter'), 90, '1', (v) =>
            v != null && Number.isInteger(v) && v >= 0 && v <= 240
              ? null
              : T('Et helt antall minutter fra 0 til 240'),
          )}{' '}
          {T('min')}
        </Setting>
      </Panel>
    );
  if (tab === 'pause') {
    // layout A «Kort» from innstillinger/pause.prototype.html (owner 2026-10-28): paid or unpaid,
    // then the length and clocking for unpaid. Betalt is auto with 0 min; the old punched mode is legacy.
    const c = breakChoiceOf(tenant);
    const unpaidLen = c.len || 30;
    const unpaidMode = (clocks: boolean): BreakMode => (clocks ? 'punched_min' : 'auto');
    body = (
      <Panel>
        {c.legacy && (
          <div className={ps.notice}>
            <p>
              <b>{T('Dere bruker den gamle innstillingen «bare stemplet pause».')}</b>{' '}
              {T(
                'Bare pausen som stemples, trekkes. Glemmer en ansatt å stemple, regnes hele vakten som arbeidstid og betales. Er pausen ubetalt hos dere, bytt til ubetalt pause: da trekkes en stemplet pause som før, og {min} min trekkes når ingen pause er stemplet.',
                { min: unpaidLen },
              )}
            </p>
            <Button
              tone="pri"
              onClick={() => save({ break_mode: 'punched_min', lunch_length_minutes: unpaidLen })}
            >
              {T('Bytt til ubetalt pause')}
            </Button>
          </div>
        )}
        <h3 className={ps.q} id="pause-paid">
          {T('Er pausen betalt?')}
        </h3>
        <div className={ps.cards} role="radiogroup" aria-labelledby="pause-paid">
          <label className={ps.card}>
            <input
              type="radio"
              name="pause-paid"
              checked={!c.legacy && c.paid}
              onChange={() => save({ break_mode: 'auto', lunch_length_minutes: 0 })}
            />
            <b>{T('Betalt')}</b>
            <span>{T('Pausen er del av arbeidstiden. Ingenting trekkes.')}</span>
          </label>
          <label className={ps.card}>
            <input
              type="radio"
              name="pause-paid"
              checked={!c.legacy && !c.paid}
              onChange={() => save({ break_mode: unpaidMode(c.clocks), lunch_length_minutes: unpaidLen })}
            />
            <b>{T('Ubetalt')}</b>
            <span>{T('Pausen trekkes fra vakter over 5,5 timer.')}</span>
          </label>
        </div>
        {!c.legacy && !c.paid && (
          <div className={ps.sub}>
            <div>
              <div className={ps.row}>
                <span>{T('Pauselengde')}</span>
                <span className={ps.len}>
                  {numIn('lunch_length_minutes', T('Pauselengde, minutter'), 80, '5', positiveBreak, (v) =>
                    // 0 means the break is working time: the Betalt card
                    v === 0 ? { break_mode: 'auto', lunch_length_minutes: 0 } : { lunch_length_minutes: v },
                  )}{' '}
                  min
                </span>
              </div>
              {fieldError.lunch_length_minutes ? (
                <span role="alert" className={ps.err}>
                  {fieldError.lunch_length_minutes}
                </span>
              ) : (
                c.len < 30 && (
                  <div className={ps.warn}>
                    {T('Loven krever minst 30 minutter pause til sammen når dagen er 8 timer eller mer.')}
                  </div>
                )
              )}
            </div>
            <div>
              <Switch
                checked={c.clocks}
                onChange={(on) => save({ break_mode: unpaidMode(on) })}
                label={T('Ansatte stempler pausen')}
              />
              <p className={ps.help}>
                {T(
                  'Valgfritt. En stemplet pause som er lengre, trekkes i stedet. Stemples ingen pause, trekkes {min} min likevel.',
                  { min: c.len },
                )}
              </p>
            </div>
          </div>
        )}
        <div className={ps.ex} data-testid="pause-example">
          <div className={ps.lab}>{T('Slik blir det')}</div>
          <div>
            {breakExample(c).map((l) => {
              const i = l.text.indexOf(l.bold);
              return (
                <p key={l.text} className={l.dim ? ps.dim : undefined}>
                  <span>
                    {i < 0 ? (
                      l.text
                    ) : (
                      <>
                        {l.text.slice(0, i)}
                        <strong>{l.bold}</strong>
                        {l.text.slice(i + l.bold.length)}
                      </>
                    )}
                  </span>
                </p>
              );
            })}
          </div>
        </div>
        <p className={ps.law}>
          <b>{T('Arbeidsmiljøloven § 10-9:')}</b>{' '}
          {T(
            'de ansatte skal ha pause når arbeidsdagen er over 5,5 timer, til sammen minst 30 minutter når den er 8 timer eller mer. Pausen er betalt arbeidstid når de ansatte ikke kan forlate arbeidsplassen fritt, eller når det ikke finnes et egnet pauserom.',
          )}
        </p>
        <p className={ps.apply}>
          {T(
            'Gjelder fra nå: vakter som alt er stemplet, regnes som før. Pauselengden lagres ikke per vakt, så en ny lengde, også 0 for betalt pause, gjelder også vakter som alt er stemplet.',
          )}
        </p>
      </Panel>
    );
  }
  if (tab === 'stempling')
    body = (
      <Panel>
        <Setting
          label={T('GPS på stempling')}
          kind="co"
          error={fieldError.is_gps_enabled}
          note={T('Ett koordinat i stemplingsøyeblikket. Ikke sporing. Håndheves ikke på serveren.')}
        >
          {sw(!!tenant?.is_gps_enabled, T('På'), T('Av'), () =>
            save({ is_gps_enabled: !tenant?.is_gps_enabled }),
          )}
        </Setting>
        <Setting
          label={T('Radius')}
          kind="co"
          error={fieldError.gps_radius_meters}
          note={T('50 m for et lokale, 300 m for en byggeplass.')}
        >
          {numIn('gps_radius_meters', T('Radius, meter'), 90, '1', (v) =>
            v != null && (v < 10 || v > 5000 || !Number.isInteger(v))
              ? T('Hele meter fra 10 til 5000')
              : null,
          )}{' '}
          m
        </Setting>
        <Setting
          label={T('Arbeidsplassen')}
          kind="co"
          error={fieldError.latitude ?? fieldError.longitude}
          note={T('Velg stedet på kartet. Koordinatene under fylles ut av seg selv, og kan rettes for hånd.')}
        >
          <Suspense fallback={<p className="hint">{T('Laster kartet …')}</p>}>
            <Kartvelger
              latitude={tenant?.latitude}
              longitude={tenant?.longitude}
              radius={tenant?.gps_radius_meters}
              onPick={(latitude, longitude) => save({ latitude, longitude })}
            />
          </Suspense>
          {numIn('latitude', T('Breddegrad'), 130, '0.000001')}{' '}
          {numIn('longitude', T('Lengdegrad'), 130, '0.000001')}
          {/* One live company has the pair the wrong way round, which puts the app's map in the
              sea off West Africa. Nothing is refused: this says so and leaves it to the manager. */}
          {placeWarning(tenant ?? null) ? (
            <p className="hint" data-testid="place-warning">
              {placeWarning(tenant ?? null)}
            </p>
          ) : null}
        </Setting>
      </Panel>
    );
  if (tab === 'modus') {
    const w = workspace;
    const legacy = legacyModeOverride(tenant?.id);
    const legacyDiffers = legacy != null && legacy !== mode;
    const save = (
      body: { scheduling_enabled?: boolean; projects_enabled?: boolean; coworker_shifts_visible?: boolean },
      done: string,
    ) =>
      ww.mutateAsync({ ...body, revision: w.revision }).then(
        () => {
          clearLegacyModeOverride(tenant?.id);
          toast(done);
        },
        (e: Error & { status?: number }) =>
          toast(
            e.status === 409
              ? T('Innstillingen ble endret av noen andre. Last siden på nytt og prøv igjen.')
              : `${T('Kunne ikke lagre')}: ${e.message}`,
          ),
      );
    body = (
      <Panel>
        <Setting
          label={T('Ansatte ser hverandres vakter')}
          kind="co"
          note={T(
            'Lagres for bedriften. Når dette er på, ser de ansatte hverandres publiserte vakter, men ikke stempling, lønn eller notater. Når det er av, ser hver ansatt bare sine egne. Ledere og administratorer ser vaktplanen uansett.',
          )}
        >
          <Switch
            checked={workspace.visibility.coworker_shifts}
            disabled={ww.isPending}
            onChange={() =>
              save(
                { coworker_shifts_visible: !workspace.visibility.coworker_shifts },
                workspace.visibility.coworker_shifts
                  ? T('Lagret: de ansatte ser nå bare sine egne vakter.')
                  : T('Lagret: de ansatte ser hverandres publiserte vakter.'),
              )
            }
            label={workspace.visibility.coworker_shifts ? T('Ja') : T('Nei')}
          />
        </Setting>
        <Setting
          label={T('Vaktplan i bruk')}
          kind="co"
          note={T(
            'Lagres for bedriften (revisjon {revision}). Tid er alltid på. Å slå av vaktplanen gjør den til «Uka»: uken viser det som er stemplet, uten publisering, bemanning og behov, og nye planlagte vakter, tilbud og publisering stopper; timer, godkjenning og lønn går som før.',
            { revision: w.revision },
          )}
        >
          <Switch
            checked={w.enabled.scheduling}
            disabled={ww.isPending}
            onChange={() =>
              save(
                { scheduling_enabled: !w.enabled.scheduling },
                w.enabled.scheduling
                  ? T(
                      'Vaktplanen er slått av. Siden heter nå Uka og viser det som er stemplet. Ingenting er slettet.',
                    )
                  : T('Vaktplanen er slått på.'),
              )
            }
            label={w.enabled.scheduling ? T('Ja') : T('Nei, bare timeregistrering')}
          />
        </Setting>
        <Setting
          label={T('Tilgjengelighet må godkjennes')}
          kind="co"
          note={T(
            'Lagres for bedriften. Når dette er på, venter tilgjengeligheten de ansatte melder på at en leder godkjenner den, og den ansatte ser «Venter på godkjenning» eller «Godkjent». Endrer den ansatte noe som er godkjent, venter endringen, og det som var godkjent gjelder til den er avgjort. Godkjenning krever rettigheten «Godkjenne tilgjengelighet» under Roller.',
          )}
        >
          <Switch
            checked={!!tenant?.availability_requires_approval}
            disabled={tw.isPending}
            onChange={() =>
              saveTenant({ availability_requires_approval: !tenant?.availability_requires_approval })
            }
            label={tenant?.availability_requires_approval ? T('Ja') : T('Nei')}
          />
        </Setting>
        {legacyDiffers && (
          <Setting
            label={T('Gammel innstilling i denne nettleseren')}
            kind="co"
            note={T(
              'Denne nettleseren hadde valgt en annen hjemskjerm før innstillingen ble lagret på serveren. Serveren gjelder nå.',
            )}
          >
            <Button
              small
              onClick={() =>
                legacy === 'timer'
                  ? save(
                      { scheduling_enabled: false, projects_enabled: false },
                      T('Lagret for bedriften: bare timeregistrering.'),
                    )
                  : save({ scheduling_enabled: true }, T('Lagret for bedriften: vaktplanen er hjem.'))
              }
            >
              {legacy === 'timer'
                ? T('Lagre «timeliste» for bedriften')
                : T('Lagre «vaktplan» for bedriften')}
            </Button>
            <Button
              small
              ghost
              onClick={() => {
                clearLegacyModeOverride(tenant?.id);
                setTick(tick + 1);
              }}
            >
              {T('Glem den')}
            </Button>
          </Setting>
        )}
      </Panel>
    );
  }
  // Innstillinger → Prosjekter (#115): the module, whether a shift must carry a project, and the
  // company's word for one. The module switch is the workspace row (revisioned); the other two
  // are the company row.
  if (tab === 'prosjekter') {
    const w = workspace;
    const saveModule = () =>
      ww.mutateAsync({ projects_enabled: !w.enabled.projects, revision: w.revision }).then(
        () => toast(w.enabled.projects ? T('Prosjekter er slått av.') : T('Prosjekter er slått på.')),
        (e: Error & { status?: number }) =>
          toast(
            e.status === 409
              ? T('Innstillingen ble endret av noen andre. Last siden på nytt og prøv igjen.')
              : `${T('Kunne ikke lagre')}: ${e.message}`,
          ),
      );
    const label = tenant?.projects_label ?? 'Prosjekt';
    body = (
      <Panel>
        <Setting
          label={T('Prosjekter i bruk')}
          kind="co"
          note={
            w.available.projects
              ? T(
                  'Lagres for bedriften. Når dette er på, velges prosjekt når en vakt lages og når de ansatte stempler inn, og timene kan rapporteres per prosjekt og kunde. Kan slås på uavhengig av vaktplanen.',
                )
              : T(
                  'Ikke tilgjengelig i denne utgaven ennå; siden Prosjekter er tom. Kan ikke slås på før den leveres.',
                )
          }
        >
          <Switch
            checked={w.enabled.projects}
            disabled={ww.isPending || (!w.enabled.projects && !w.available.projects)}
            onChange={saveModule}
            label={w.enabled.projects ? T('Ja') : w.available.projects ? T('Nei') : T('Ikke tilgjengelig')}
          />
        </Setting>
        <Setting
          label={T('Prosjekt påkrevd')}
          kind="co"
          note={T(
            'Lagres for bedriften. Når dette er på, må en vakt ha et prosjekt når den lages i Vaktplan, og Kontroller under Rapporter lister godkjente timer uten prosjekt før filen lastes ned. En stempling uten plan registreres uansett.',
          )}
        >
          <Switch
            checked={!!tenant?.projects_required}
            disabled={tw.isPending || !w.enabled.projects}
            onChange={() => saveTenant({ projects_required: !tenant?.projects_required })}
            label={tenant?.projects_required ? T('Ja') : T('Nei, valgfritt')}
          />
        </Setting>
        <Setting
          label={T('Hva kaller dere det?')}
          kind="co"
          note={T(
            'Ordet brukes overalt i Timefly for denne bedriften: i menyen, på vakten, ved stempling og i rapportene. Et vaktfirma sier gjerne «objekt», en håndverker «oppdrag».',
          )}
        >
          <select
            className="inp"
            aria-label={T('Betegnelse')}
            value={label}
            disabled={tw.isPending}
            onChange={(e) =>
              saveTenant({ projects_label: e.target.value as 'Prosjekt' | 'Oppdrag' | 'Objekt' })
            }
          >
            <option value="Prosjekt">{T('Prosjekt')}</option>
            <option value="Oppdrag">{T('Oppdrag')}</option>
            <option value="Objekt">{T('Objekt')}</option>
          </select>
        </Setting>
        <Setting
          label={T('Varsel på budsjettet')}
          kind="co"
          note={T(
            'Lagres for bedriften. Når timene på et prosjekt med budsjett når denne andelen, vises et varsel på prosjektet og Hugin sier fra.',
          )}
        >
          <select
            className="inp"
            aria-label={T('Varsel på budsjettet')}
            value={tenant?.budget_warn_percent ?? 80}
            disabled={tw.isPending || !w.enabled.projects}
            onChange={(e) =>
              saveTenant({
                budget_warn_percent: Number(e.target.value) === 80 ? null : Number(e.target.value),
              })
            }
          >
            {[50, 60, 70, 75, 80, 85, 90, 95, 100].map((p) => (
              <option key={p} value={p}>
                {p} %
              </option>
            ))}
          </select>
        </Setting>
      </Panel>
    );
  }
  if (tab === 'bemanning') {
    const setCell = (d: number, h: number, v: number) => {
      saveNeed({ ...need, [d]: { ...(need[d] ?? {}), [h]: v } });
    };
    const copyRow = (from: number, to: number[]) => {
      const next = { ...need };
      for (const d of to) next[d] = { ...(need[from] ?? {}) };
      saveNeed(next, T('Kopiert'));
    };
    body = (
      <>
        <div className="al info">
          <span>ⓘ</span>
          <span>
            {T(
              'Personer som trengs per time og ukedag. Dagen tegner oppsatt mot dette. Lagres for hele bedriften.',
            )}
          </span>
        </div>
        <Panel flush>
          <div className="tw">
            {/* the weekday stays pinned while the hours scroll, and every cell names its day and hour (#267 SET-23) */}
            <table className="t stneed" data-testid="need-grid">
              <caption className="sr">{T('Personer som trengs per ukedag og time')}</caption>
              <thead>
                <tr>
                  <th scope="col">{T('Dag')}</th>
                  {HOURS.map((h) => (
                    <th key={h} className="n" style={{ padding: '8px 4px' }}>
                      {h}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {DN.map((n, d) => (
                  <tr key={n}>
                    <th scope="row">
                      <b>{n}</b>
                    </th>
                    {HOURS.map((h) => (
                      <td key={h} className="n" style={{ padding: '4px 2px' }}>
                        <input
                          className="inp"
                          type="number"
                          min={0}
                          aria-label={T('{day} kl. {hour}', {
                            day: DNL[d] ?? n,
                            hour: String(h).padStart(2, '0'),
                          })}
                          style={{ width: 38, padding: '4px 3px', textAlign: 'center' }}
                          value={need[d]?.[h] ?? ''}
                          onChange={(e) => setCell(d, h, Number(e.target.value) || 0)}
                        />
                      </td>
                    ))}
                    <td>
                      {d === 0 && (
                        <Button small onClick={() => copyRow(0, [1, 2, 3, 4])}>
                          {T('Til hverdager')}
                        </Button>
                      )}
                      {d === 5 && (
                        <Button small onClick={() => copyRow(5, [6])}>
                          {T('Til søndag')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </>
    );
  }
  if (tab === 'team') {
    const teams = (teamsQ.data ?? []).filter((t) => t.name);
    // active people only; a deactivated member no longer blocks deleting the team, since the
    // server removes the memberships with it (teamController deleteTeam, P0.17)
    const count = (id: number) =>
      (usersQ.data ?? []).filter((u) => u.active_status && (u.teams ?? []).some((t) => t.id === id)).length;
    body = (
      <Panel
        title={T('Team')}
        sub={T('en gruppering og et filter, aldri rettigheter')}
        actions={
          <Button
            small
            onClick={() =>
              tmw.create.mutateAsync(`Nytt team ${teams.length + 1}`).then(() => toast(T('Team opprettet')))
            }
          >
            {T('Nytt team')}
          </Button>
        }
        flush
      >
        {teams.length ? (
          <table className="t">
            <thead>
              <tr>
                <th>{T('Team')}</th>
                <th className="n">{T('Personer')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.id}>
                  <td>
                    <input
                      className="inp"
                      aria-label={T('Team {name}', { name: t.name })}
                      key={t.name}
                      defaultValue={t.name}
                      style={{ minWidth: 200 }}
                      onBlur={(e) =>
                        e.target.value.trim() &&
                        e.target.value !== t.name &&
                        tmw.update
                          .mutateAsync({ id: t.id, name: e.target.value.trim() })
                          .then(() => toast(T('Teamet heter nå {name}', { name: e.target.value.trim() })))
                      }
                    />
                  </td>
                  <td className="n">{count(t.id)}</td>
                  <td>
                    <Button
                      small
                      disabled={count(t.id) > 0}
                      title={count(t.id) ? T('Teamet har personer. Flytt dem først.') : ''}
                      onClick={() => tmw.remove.mutateAsync(t.id).then(() => toast(T('Fjernet')))}
                    >
                      {T('Fjern')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>{T('Ingen team.')}</Empty>
        )}
      </Panel>
    );
  }
  if (tab === 'typer') {
    const types = typesQ.data ?? [];
    const ats = absTypesQ.data ?? [];
    body = (
      <>
        <Panel
          title={T('Vakttyper')}
          sub={T('bedriftens egne, {n} stk', { n: types.length })}
          actions={
            <Button
              small
              onClick={() => setTypeDraft({ name: '', color_code: '#2f5fc4', from: '08:00', to: '16:00' })}
            >
              {T('Ny vakttype')}
            </Button>
          }
          flush
        >
          <table className="t" data-testid="shift-types">
            <thead>
              <tr>
                <th>{T('Navn')}</th>
                <th>{T('Tid')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {types.map((x) => (
                <tr key={x.id}>
                  <td>
                    <span className="dot" style={{ background: x.color_code || 'var(--control)' }} />
                    <b>{x.name}</b>
                    {x.id === 1 && <small>{T('Typen stemplingsklokka gir en vakt uten plan')}</small>}
                  </td>
                  <td className="mono">
                    {typeTime(x.start_time)}–{typeTime(x.end_time)}
                  </td>
                  <td>
                    <Button
                      small
                      onClick={() =>
                        setTypeDraft({ ...x, from: typeTime(x.start_time), to: typeTime(x.end_time) })
                      }
                    >
                      {T('Endre')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel title={T('Feriedager')} sub={T('hvordan ferie trekkes fra saldoen')}>
          <Setting
            label={T('Ferien telles i')}
            kind="co"
            note={T(
              'Gjelder fravær som trekkes etter at du bytter: det som alt er trukket, gis tilbake med nøyaktig det antallet som ble trukket. Saldoene og årskvoten på hver ansatt regnes ikke om; juster dem under Ansatte. Den årlige påfyllingen gir 25 virkedager eller 21 arbeidsdager.',
            )}
          >
            <select
              className="inp"
              aria-label={T('Ferien telles i')}
              value={tenant?.vacation_day_basis ?? 'virkedager'}
              onChange={(e) => save({ vacation_day_basis: e.target.value as 'virkedager' | 'arbeidsdager' })}
            >
              <option value="virkedager">{T('Virkedager, mandag til lørdag: 25 dager (loven)')}</option>
              <option value="arbeidsdager">{T('Arbeidsdager, mandag til fredag: 21 dager')}</option>
            </select>
          </Setting>
        </Panel>
        <Panel
          title={T('Fraværstyper')}
          sub={`${ats.some((a) => a.is_builtin) ? `${T('seedet katalog')}, ` : ''}${T('{n} aktive av {total}', { n: ats.filter((a) => a.is_active).length, total: ats.length })}`}
          actions={
            <Button
              small
              onClick={() =>
                setAbsDraft({
                  name: '',
                  type: 'date',
                  is_paid: false,
                  is_deductible: false,
                  is_sick_day: false,
                  is_active: true,
                })
              }
            >
              {T('Egendefinert type')}
            </Button>
          }
          flush
        >
          <div className="tw">
            <table className="t" data-testid="absence-types">
              <thead>
                <tr>
                  <th>{T('Type')}</th>
                  <th>{T('Enhet')}</th>
                  <th>{T('Lønn')}</th>
                  <th>{T('Saldo')}</th>
                  <th>{T('Grenser')}</th>
                  <th>{T('Leveres som')}</th>
                  <th>{T('På')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {ats.map((a) => (
                  <tr key={a.id} style={a.is_active ? undefined : { opacity: 0.55 }}>
                    <td>
                      <span className="dot" style={{ background: a.color_code || 'var(--slate-l)' }} />
                      <b>{typeName(a)}</b>
                      <small className="mono">
                        {a.key ?? T('egendefinert')}
                        {a.is_sick_day ? `, ${T('sykefravær')}` : ''}
                      </small>
                      {ownVacationWithoutDeduction(a) && (
                        <Chip tone="warn" small>
                          {T('bedriftens egen, trekker ikke feriedager')}
                        </Chip>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {unitText(a, tenant?.vacation_day_basis ?? 'virkedager')}
                    </td>
                    <td style={{ fontSize: 12 }}>{payerText(a)}</td>
                    <td style={{ fontSize: 12 }}>{balanceText(a)}</td>
                    <td style={{ fontSize: 12, color: 'var(--ink3)' }}>
                      {limitsText(a).join(', ') || (lawNote(a) ? '' : '–')}
                      {lawNote(a) && <small>{lawNote(a)}</small>}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {a.a_melding ? (
                        `${T('A-melding')}: ${a.a_melding}`
                      ) : a.salary_type_code ? (
                        <span className="mono">
                          {T('Lønnsart')} {a.salary_type_code}
                        </span>
                      ) : (
                        <Chip tone={a.is_paid ? 'warn' : 'neu'} small>
                          {T('lønnsart ikke satt')}
                        </Chip>
                      )}
                    </td>
                    <td>
                      <Switch
                        checked={a.is_active}
                        label={a.is_active ? T('På') : T('Av')}
                        title={`${a.name}: ${a.is_active ? T('slå av') : T('slå på')}`}
                        onChange={(on) => toggleAbs(a, on)}
                      />
                    </td>
                    <td>
                      <Button small onClick={() => setAbsDraft(a)}>
                        {T('Endre')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
        <p className="hint" style={{ maxWidth: '66ch' }}>
          {T(
            'Lønnsart er bedriftens eget nummer for fraværet i lønnssystemet, for eksempel Tripletex. Timefly fyller ikke inn noe: skriv inn nummeret under Endre. Uten lønnsart står fraværet uten nummer i lønnsfilen.',
          )}
        </p>
        <p className="hint" style={{ maxWidth: '66ch' }}>
          {T(
            'Vakttyper er bedriftens eget vokabular. Fraværstyper er et rapporteringsvokabular og redigeres her.',
          )}
        </p>
      </>
    );
  }
  if (tab === 'lonn') {
    const rates = ratesOf(labour.rates);
    // a field left unchanged sends nothing: the same figure again would only add a write (#272 L-W10)
    const saveRates = (patch: Partial<typeof rates>) =>
      (Object.keys(patch) as (keyof typeof rates)[]).some((k) => patch[k] !== rates[k]) &&
      saveLabour.mutate(
        { revision: labour.revision, ...ratesTo(patch) },
        { onSuccess: () => toast(T('Lagret')), onError: (e) => toast(errorText(e)) },
      );
    const example = costOf(1000, rates);
    const kr2 = (n: number) => n.toLocaleString('nb-NO', { maximumFractionDigits: 2 });
    const pts = ptQ.data ?? [];
    const rules = rrQ.data ?? [];
    const dayName = (d: number) => DN[(d + 6) % 7] ?? String(d);
    const ruleText = (r: ApiRateRule) =>
      r.threshold != null
        ? `${T('over')} ${num(Number(r.threshold))} ${T('t per uke')}`
        : `${dayName(r.day)} ${r.start_time?.slice(0, 5) ?? ''}–${r.end_time?.slice(0, 5) ?? ''}`;
    body = (
      <>
        <SettingsOverview />
        {/* What the company pays on top of wages. The designer's report, 2026-09-21: the
            employer's contribution is 14.1% and pension is at least 2%, most companies use the
            minimum, so the rate belongs to the company rather than to the code. It was a
            constant, and pension was missing entirely, so every labour figure ran about 2%
            light. Stored for the company on the server since 2026-09-25 (api/labour.ts). */}
        <Panel
          title={T('Arbeidsgiverkostnader')}
          sub={T('legges på lønnen i bemanning mot omsetning og i vaktplanen')}
        >
          <Setting
            label={T('Arbeidsgiveravgift')}
            kind="co"
            note={T('14,1 % i sone 1. Lavere i sone 2 til 5; se Skatteetatens soneoversikt.')}
          >
            <input
              className="inp"
              type="number"
              step="0.1"
              min="0"
              max="100"
              style={{ width: 90 }}
              aria-label={T('Arbeidsgiveravgift, prosent')}
              key={`agaPct-${rates.agaPct}`} // remount when the figure arrives or changes, or the first render's value sticks (issue #80)
              defaultValue={rates.agaPct}
              onBlur={(e) => saveRates({ agaPct: Number(e.target.value) })}
            />{' '}
            %
          </Setting>
          <Setting
            label={T('Pensjon')}
            kind="co"
            note={T('Obligatorisk tjenestepensjon, minimum 2 % av lønnen.')}
          >
            <input
              className="inp"
              type="number"
              step="0.1"
              min="0"
              max="100"
              style={{ width: 90 }}
              aria-label={T('Pensjon, prosent')}
              key={`pensionPct-${rates.pensionPct}`} // remount when the figure arrives or changes, or the first render's value sticks (issue #80)
              defaultValue={rates.pensionPct}
              onBlur={(e) => saveRates({ pensionPct: Number(e.target.value) })}
            />{' '}
            %
          </Setting>
          <Setting
            label={T('Feriepenger')}
            kind="co"
            note={T('10,2 % etter ferieloven, 12 % med femte ferieuke i tariffavtale.')}
          >
            <input
              className="inp"
              type="number"
              step="0.1"
              min="0"
              max="100"
              style={{ width: 90 }}
              aria-label={T('Feriepenger, prosent')}
              key={`holidayPct-${rates.holidayPct}`} // remount when the figure arrives or changes, or the first render's value sticks (issue #80)
              defaultValue={rates.holidayPct}
              onBlur={(e) => saveRates({ holidayPct: Number(e.target.value) })}
            />{' '}
            %
          </Setting>
          <p className="hint" style={{ padding: '0 12px' }} data-testid="rates-example">
            {T(
              '1 000 kr i lønn koster {total} kr: {wage} + {hol} feriepenger + {pen} pensjon + {aga} arbeidsgiveravgift.',
              {
                total: kr2(example.total),
                wage: kr2(example.wage),
                hol: kr2(example.holiday),
                pen: kr2(example.pension),
                aga: kr2(example.aga),
              },
            )}
          </p>
          {/* the same rates as PowerOffice Go has them, taken one at a time (#183 phase 3) */}
          <PowerOfficeRates
            aga={rates.agaPct}
            holiday={rates.holidayPct}
            onUse={saveRates}
            sys={tenant?.payroll_system === 'tripletex' ? TRIPLETEX : POWEROFFICE}
          />
        </Panel>
        {/* unused overtime in the timebank (owner, 2026-09-23, plan 0027 G2 slice 3) */}
        {/* #110: which file Rapporter makes; the pay codes below are that system's numbers */}
        {/* Kom i gang «Velg lønnssystem» links here */}
        <div id="lonnssystem" />
        <Panel title={T('Lønnssystem')} sub={T('filen som lastes ned under Rapporter')}>
          <Setting
            label={T('Lønnssystem')}
            error={fieldError.payroll_system}
            kind="co"
            note={T(
              'Systemet dere kjører lønn i. Alle lønnsartnumre i Timefly, her og på lønnsartene og fraværstypene, skal være numrene fra dette systemet.',
            )}
          >
            <select
              className="inp"
              aria-label={T('Lønnssystem')}
              value={chosen(tenant) ? (systemOf(tenant) ?? '') : 'unchosen'}
              onChange={(e) =>
                save({
                  payroll_system: (e.target.value || null) as 'tripletex' | 'poweroffice' | 'visma' | null,
                })
              }
            >
              {/* a new company that has not picked (2026-11-04): shown, never offered back */}
              {!chosen(tenant) && (
                <option value="unchosen" disabled>
                  {T('Velg lønnssystem')}
                </option>
              )}
              {/* Oppsett's «Annet, eller vet ikke» (#288 review fix 1): no file or link is the company's own */}
              <option value="">{T('Annet, eller vet ikke')}</option>
              <option value="tripletex">Tripletex</option>
              <option value="poweroffice">{T('PowerOffice Go (beta)')}</option>
              <option value="visma">{T('Visma (Visma-fil støttes ikke ennå)')}</option>
            </select>
          </Setting>
        </Panel>
        <div id="lonnsart" />
        <Panel title={T('Lønnsart for vanlige timer')} sub={T('grunnlønnen i lønnsfilen og i Tripletex')}>
          <Setting
            label={T('Lønnsart for vanlige timer')}
            error={fieldError.regular_pay_code}
            kind="co"
            note={T(
              'Nummeret lønnssystemet har for timelønn; i Tripletex er standarden 2001 Timelønn. Står feltet tomt, får timene ingen lønnsart, og Kontroller stopper dem.',
            )}
          >
            <input
              className="inp"
              style={{ width: 110 }}
              aria-label={T('Lønnsart for vanlige timer')}
              placeholder={tenant?.payroll_system === 'tripletex' ? '2001' : ''}
              key={`regcode-${tenant?.regular_pay_code ?? ''}`}
              defaultValue={tenant?.regular_pay_code ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim() || null;
                if (v !== (tenant?.regular_pay_code ?? null)) save({ regular_pay_code: v });
              }}
            />
          </Setting>
          {/* #26: the «(lønn)» lines for 1 and 17 May go out under the code above, the tillegg under its own */}
          <Setting
            label={T('Lønnsart for tillegg 1. og 17. mai')}
            error={fieldError.hoytid_pay_code}
            kind="co"
            note={T(
              'Tillegget for timer arbeidet 1. og 17. mai. Står feltet tomt, får linjen ingen lønnsart, og Kontroller stopper den. Fri med lønn de dagene går under lønnsarten for vanlige timer.',
            )}
          >
            <input
              className="inp"
              style={{ width: 110 }}
              aria-label={T('Lønnsart for tillegg 1. og 17. mai')}
              key={`hoytidcode-${tenant?.hoytid_pay_code ?? ''}`}
              defaultValue={tenant?.hoytid_pay_code ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim() || null;
                if (v !== (tenant?.hoytid_pay_code ?? null)) save({ hoytid_pay_code: v });
              }}
            />
          </Setting>
          {/* #143: off by default; the person is marked on Ansatte → Avtale */}
          <Setting
            label={T('Ingen tillegg for fastlønte unntatt arbeidstidsreglene')}
            error={fieldError.exempt_salaried_no_supplements}
            kind="co"
            note={T(
              'Gjelder den som har fastlønn og er unntatt arbeidstidsreglene på ansattkortet (ledende eller særlig uavhengig stilling, arbeidsmiljøloven § 10-12). De får aldri overtid. Når dette er på, får de heller ikke kvelds-, natt- eller helgetillegg; lønnen er den faste.',
            )}
          >
            <Switch
              checked={!!tenant?.exempt_salaried_no_supplements}
              disabled={tw.isPending}
              onChange={() =>
                save({ exempt_salaried_no_supplements: !tenant?.exempt_salaried_no_supplements })
              }
              label={tenant?.exempt_salaried_no_supplements ? T('Ja') : T('Nei')}
            />
          </Setting>
          {/* plan 0027 batch B: on (plain pay) is the default since the owner's decision of 2026-10-20 */}
          <Setting
            label={T('Merarbeid for deltidsansatte på fastlønn')}
            error={fieldError.merarbeid_plain_pay}
            kind="co"
            note={T(
              'Timer over avtalt uke hos en deltidsansatt med fastlønn, opp til bedriftens uke. Loven gir ordinær lønn for merarbeid, men mange avtaler gir overtidstillegg. Av: betales som overtid fra avtalt uke. På: betales med timelønnen på en egen linje, Merarbeid. Med fleksitidsavtale går timene i timebanken.',
            )}
          >
            <Switch
              checked={tenant?.merarbeid_plain_pay !== false}
              disabled={tw.isPending}
              onChange={() => save({ merarbeid_plain_pay: tenant?.merarbeid_plain_pay === false })}
              label={tenant?.merarbeid_plain_pay !== false ? T('Ordinær lønn') : T('Overtid')}
            />
          </Setting>
        </Panel>
        <Panel title={T('Ubrukt avspasering')} sub={T('overtid i timebanken som ikke er tatt ut')}>
          <Setting
            label={T('Ubrukte timer')}
            error={fieldError.avspasering_unused}
            kind="co"
            note={T(
              'Utbetales: Rapporter-siden minner om det i perioden med datoen, og timene betales med Utbetal på ansattkortet. Overføres: timene blir stående. Den som slutter, får alltid timene utbetalt.',
            )}
          >
            <select
              className="inp"
              aria-label={T('Ubrukte timer')}
              value={tenant?.avspasering_unused ?? ''}
              onChange={(e) =>
                save({ avspasering_unused: (e.target.value || null) as 'pay' | 'carry' | null })
              }
            >
              <option value="">{T('Ikke valgt')}</option>
              <option value="pay">{T('Utbetales på en dato')}</option>
              <option value="carry">{T('Overføres til neste år')}</option>
            </select>
          </Setting>
          {tenant?.avspasering_unused === 'pay' && (
            <Setting
              label={T('Utbetalingsdato')}
              kind="co"
              error={fieldError.avspasering_payout_day}
              note={T('Dag og måned, for eksempel 31.12.')}
            >
              <input
                className="inp"
                style={{ width: 90 }}
                aria-label={T('Utbetalingsdato, dag og måned')}
                placeholder="31.12"
                key={`payday-${tenant?.avspasering_payout_day ?? ''}`}
                defaultValue={
                  tenant?.avspasering_payout_day
                    ? `${tenant.avspasering_payout_day.slice(3)}.${tenant.avspasering_payout_day.slice(0, 2)}`
                    : ''
                }
                onBlur={(e) => {
                  const m = /^(\d{1,2})\.(\d{1,2})$/.exec(e.target.value.trim());
                  const v =
                    e.target.value.trim() === ''
                      ? null
                      : m
                        ? `${m[2]?.padStart(2, '0')}-${m[1]?.padStart(2, '0')}`
                        : 'x';
                  if (v !== (tenant?.avspasering_payout_day ?? null)) save({ avspasering_payout_day: v });
                }}
              />
            </Setting>
          )}
          <Setting
            label={T('Lønnsart for utbetalt avspasering')}
            error={fieldError.avspasering_payout_code}
            kind="co"
            note={T(
              'Koden lønnssystemet bruker for timene når de utbetales. Uten kode stopper eksporten under Rapporter.',
            )}
          >
            <input
              className="inp"
              style={{ width: 110 }}
              aria-label={T('Lønnsart for utbetalt avspasering')}
              key={`paycode-${tenant?.avspasering_payout_code ?? ''}`}
              defaultValue={tenant?.avspasering_payout_code ?? ''}
              onBlur={(e) => {
                const v = e.target.value.trim() || null;
                if (v !== (tenant?.avspasering_payout_code ?? null)) save({ avspasering_payout_code: v });
              }}
            />
          </Setting>
        </Panel>
        <div id="satser" />
        <Panel
          title={T('Lønnsarter og satser')}
          sub={T('det som legges på grunnlønnen')}
          actions={
            <Button
              small
              onClick={() => setTypeEdit({ name: '', code: '', rateType: 'extra_per_hour', value: '0' })}
            >
              <Icon name="plus" />
              {T('Ny lønnsart')}
            </Button>
          }
          flush
        >
          <table className="t" data-testid="payroll-types">
            <thead>
              <tr>
                <th>{T('Kode')}</th>
                <th>{T('Lønnsart')}</th>
                <th>{T('Regler')}</th>
                <th className="n">{T('Sats')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pts.map((p) => {
                const rs = rules.filter((r) => r.payroll_type_id === p.id);
                // AML §10-6 (11): overtime is paid at least 40 %; a lower overtime rate is shown,
                // never refused, since an agreement may say otherwise (M9)
                const lowOvertime = overtimeBelowLaw(p, rs);
                return (
                  <tr key={p.id}>
                    <td className="mono">{p.code}</td>
                    <td>
                      <b>{p.name}</b>
                      {p.paysWholeHour && (
                        <div className="hint" data-testid="pays-whole-hour">
                          {T('Betaler hele timen, grunnlønn og tillegg')}
                        </div>
                      )}
                      {p.yieldsTo != null && (
                        <div className="hint" data-testid="yields-to">
                          {T('Ikke i timer med {name}', {
                            name: pts.find((x) => x.id === p.yieldsTo)?.name ?? '–',
                          })}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink3)' }}>
                      {rs.length
                        ? rs.map((r) => (
                            // 8 px between wrapped rows, so each «Slett» keeps its whole 44 px hit area (#272 L-W08)
                            <span key={r.id} className="chip neu sm" style={{ margin: '0 4px 8px 0' }}>
                              {ruleText(r)}{' '}
                              <button
                                type="button"
                                className="x"
                                // the pay type too: two types can carry the same day and hours (#267 SET-21)
                                aria-label={T('Slett regelen {rule} for {type}', {
                                  rule: ruleText(r),
                                  type: p.name,
                                })}
                                style={{
                                  border: 0,
                                  background: 'none',
                                  cursor: 'pointer',
                                  color: 'inherit',
                                  padding: '0 2px',
                                }}
                                onClick={() =>
                                  window.confirm(
                                    T(
                                      'Slette regelen {rule} for {type}? Den kan ikke angres; lag den på nytt med Ny regel.',
                                      {
                                        rule: ruleText(r),
                                        type: p.name,
                                      },
                                    ),
                                  ) &&
                                  pw.removeRule.mutateAsync(r.id).then(
                                    () => toast(T('Regelen er slettet')),
                                    (e) => toast(errorText(e)),
                                  )
                                }
                              >
                                <Icon name="close" />
                              </button>
                            </span>
                          ))
                        : '–'}
                      {lowOvertime && (
                        <div
                          className="chip warn sm"
                          style={{ marginTop: 4 }}
                          data-testid="overtime-below-law"
                        >
                          {T('Overtid under 40 % (AML §10-6)')}
                        </div>
                      )}
                      <Button
                        small
                        ghost
                        onClick={() =>
                          setRuleDraft({
                            payroll_type_id: p.id,
                            day: 1,
                            time_from: '18:00',
                            time_to: '22:00',
                            priority: 1,
                          })
                        }
                      >
                        <Icon name="plus" />
                        {T('Ny regel')}
                      </Button>
                    </td>
                    <td className="n">
                      {p.rateType === 'percent'
                        ? `${Number(p.value).toLocaleString('nb-NO')} %`
                        : `${Number(p.value).toLocaleString('nb-NO', { minimumFractionDigits: 2 })} kr/t`}
                    </td>
                    <td>
                      <Button small onClick={() => setTypeEdit(p)}>
                        {T('Endre sats')}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
        <p className="hint" style={{ maxWidth: '66ch' }}>
          {T(
            'To regler som overlapper telles begge, med mindre den ene lønnsarten er satt til å vike for den andre. Lønnsarten kan ikke slettes fra denne ruten; den gamle appen har heller ingen sletting som virker.',
          )}
        </p>
        <Panel>
          <Setting
            label={T('Lønnsperiode')}
            kind="soon"
            note={T('Månedlig lønnsperiode gjelder for alle. Kan ikke endres i denne utgaven.')}
          >
            <span style={{ color: 'var(--ink3)' }}>{T('Månedlig')}</span>
          </Setting>
          <Setting
            label={T('Eksport')}
            kind="std"
            note={T('Filen til lønnssystemet og CSV-rapporten lastes ned under Rapporter.')}
          >
            <span style={{ color: 'var(--ink3)' }}>{T('CSV')}</span>
          </Setting>
        </Panel>
        {/* the direct link of the company's payroll system (#110); Visma has none */}
        <div id="integrasjon">
          {/* #297 ONB-23/24: the administrator's own draft of the link's setup, before or beside the link */}
          {tenant?.me?.is_admin === true && <SetupDraftPanel />}
          {systemOf(tenant) === 'tripletex' && <TripletexConnect />}
          {tenant?.payroll_system === 'poweroffice' && <PowerOfficeConnect />}
        </div>
        {/* works with no API link: the numbers against Go's own employee list (#183) */}
        {tenant?.payroll_system === 'poweroffice' && tenant?.me?.is_admin && <PowerOfficeFileCheck />}
      </>
    );
  }
  // #294 ONB-08: keyed on the saved profile, so a fresh read replaces the draft and a refused save keeps it
  if (tab === 'regelprofil' && tenant)
    body = <Regelprofil key={JSON.stringify(tenant.rule_profile ?? null)} tenant={tenant} />;
  if (tab === 'oppgaver') body = <Oppgaver />;
  if (tab === 'ferdigheter') body = <FerdigheterTab />;
  if (tab === 'roller') body = <Roller />;
  if (tab === 'varsler') body = <Varsler value={tenant?.push_settings} save={save} />;
  if (tab === 'ai') body = me?.is_admin === true ? <AiHistorikk /> : null;
  if (tab === 'endringer') body = me?.is_admin === true ? <Endringer /> : null;

  const saveType = async () => {
    if (!typeDraft?.name) return toast(T('Navn mangler'));
    const body = {
      name: typeDraft.name,
      color_code: typeDraft.color_code ?? null,
      start_time: `1970-01-01T${typeDraft.from}:00.000Z`,
      end_time: `1970-01-01T${typeDraft.to}:00.000Z`,
    };
    try {
      if (typeDraft.id) await stw.update.mutateAsync({ id: typeDraft.id, body });
      else await stw.create.mutateAsync(body);
      toast(T('Lagret'));
      setTypeDraft(null);
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${(e as Error).message}`);
    }
  };
  const toggleAbs = async (a: ApiAbsenceType, on: boolean) => {
    try {
      const { id, ...rest } = a;
      await atw.update.mutateAsync({ id, body: { ...rest, is_active: on } });
      toast(on ? T('{name} er på', { name: a.name }) : T('{name} er av', { name: a.name }));
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${(e as Error).message}`);
    }
  };
  const saveAbs = async () => {
    if (!absDraft?.name) return toast(T('Navn mangler'));
    try {
      const { id, ...rest } = absDraft;
      if (id) await atw.update.mutateAsync({ id, body: rest });
      else await atw.create.mutateAsync(rest);
      toast(T('Lagret'));
      setAbsDraft(null);
    } catch (e) {
      toast(`${T('Kunne ikke lagre')}: ${(e as Error).message}`);
    }
  };
  const fld = (label: string, c: React.ReactNode) => (
    <div className="fld">
      <span className="lbl">{label}</span>
      {c}
    </div>
  );

  const group = groupOf(tab);
  const section = SECTIONS[tab] && (!ADMIN_READ.includes(tab) || me?.is_admin === true) ? tab : null;
  const sectionBody = section && (
    <>
      {['bedrift', 'arbeidstid', 'pause', 'stempling', 'team'].includes(tab) && (
        <p className="hint" style={{ margin: '0 0 8px' }}>
          {T('Endringer lagres automatisk når du går ut av feltet.')}
        </p>
      )}
      {/* Modus, Lønn and Roller are written by the Administrator only (server.js: authenticateToken
          ['admin']); another role sees them read-only instead of editing and being refused (M2) */}
      {ADMIN_TABS.includes(tab) && me?.is_admin !== true ? (
        <>
          <div className="al info" data-testid="read-only">
            <span>i</span>
            <span>{T('Bare administrator kan endre denne siden.')}</span>
          </div>
          <fieldset disabled className="ro">
            {body}
          </fieldset>
        </>
      ) : (
        body
      )}
      {tab !== 'typer' &&
        tab !== 'roller' &&
        tab !== 'oppgaver' &&
        tab !== 'ferdigheter' &&
        tab !== 'regelprofil' &&
        !ADMIN_READ.includes(tab) && (
          <p className="hint" style={{ maxWidth: '66ch' }}>
            <b>{T('Bedrift')}</b> {T('der én verdi for alle er riktig.')} <b>{T('Standard')}</b>{' '}
            {T('der bedriften setter et utgangspunkt som ansattkortet overstyrer.')}{' '}
            <b>{T('Ikke tilgjengelig ennå')}</b>{' '}
            {T('der verdien ikke kan lagres i denne utgaven. Loven vinner alltid.')}
          </p>
        )}
    </>
  );

  return (
    <div className="stpage">
      <PageHead
        icon="sliders"
        title={T('Innstillinger')}
        sub={T('Bedriftens oppsett, samlet utenfor den daglige driften.')}
        actions={
          // the quiet way back into setup: the administrator's «Kom i gang», anyone else the guide (#267 V1.1)
          me?.is_admin === true ? (
            <>
              <button type="button" className="mlink" onClick={() => useWelcome.getState().show()}>
                <Icon name="help" />
                {T('Oppsett og veiledning')}
              </button>
              {/* the first-run setup once more, from its first step (owner 2026-11-01) */}
              {tenant && !tenant.setup_step && (
                <button
                  type="button"
                  className="mlink"
                  disabled={tw.isPending}
                  onClick={() =>
                    tw.mutateAsync({ id: tenant.id, body: { setup_step: 'settings' } }).then(
                      () => go('/oppsett'),
                      (e) => toast(`${T('Kunne ikke lagre')}: ${errorText(e)}`),
                    )
                  }
                >
                  <Icon name="refresh" />
                  {T('Kjør oppsettet på nytt')}
                </button>
              )}
            </>
          ) : (
            <a className="mlink" href={helpUrl('/innstillinger')} target="_blank" rel="noreferrer">
              <Icon name="help" />
              {T('Veiledning')}
            </a>
          )
        }
      >
        <FollowUp />
        <Tabs
          label={T('Innstillinger')}
          value={group}
          items={GROUPS.map((g) => ({
            key: g.key,
            label: T(g.label),
            icon: g.icon,
            // a group of one section opens it
            to: `/innstillinger/${g.sections.length === 1 ? g.sections[0] : g.key}`,
          }))}
        />
        {/* a phone picks the group from a native list instead of a row of tabs that runs off the edge (#272 SS-19) */}
        <label className="stsel">
          <span className="sr">{T('Kategori')}</span>
          <select
            className="inp"
            value={group}
            onChange={(e) => {
              const g = GROUPS.find((x) => x.key === e.target.value);
              if (g) go(`/innstillinger/${g.sections.length === 1 ? g.sections[0] : g.key}`);
            }}
          >
            {GROUPS.map((g) => (
              <option key={g.key} value={g.key}>
                {T(g.label)}
              </option>
            ))}
          </select>
        </label>
      </PageHead>
      <LocalFiguresOffer />
      <TabPanel tab={group}>
        <GroupSummary group={group} />
        {GROUPS.find((g) => g.key === group)?.sections.length === 1 ? (
          // one section: its editor is the page, no list around it (#267)
          <section className="mcard stone">{sectionBody ?? body}</section>
        ) : (
          <SectionList group={group} open={section} hide={me?.is_admin === true ? [] : ADMIN_READ}>
            {sectionBody}
          </SectionList>
        )}
      </TabPanel>

      <Drawer
        open={!!ruleDraft}
        onClose={() => setRuleDraft(null)}
        title={T('Ny regel')}
        footer={
          <>
            <span className="sp" />
            <Button onClick={() => setRuleDraft(null)}>{T('Avbryt')}</Button>
            <Button
              tone="pri"
              onClick={() =>
                ruleDraft?.payroll_type_id &&
                pw.createRule
                  .mutateAsync({
                    ...ruleDraft,
                    payroll_type_id: ruleDraft.payroll_type_id,
                    day: ruleDraft.day ?? 1,
                    time_from: ruleDraft.threshold != null ? null : `${ruleDraft.time_from ?? '00:00'}:00`,
                    // stored as typed: ':59' was appended, so 18:00 to 22:00 also paid the 22:00 hour (M7);
                    // empty means to midnight
                    time_to:
                      ruleDraft.threshold != null
                        ? null
                        : ruleDraft.time_to
                          ? `${ruleDraft.time_to}:00`
                          : '24:00:00',
                  })
                  .then(
                    () => {
                      toast(T('Regelen er lagret'));
                      setRuleDraft(null);
                    },
                    (e) => toast(errorText(e)),
                  )
              }
            >
              {T('Lagre')}
            </Button>
          </>
        }
      >
        {ruleDraft && (
          <>
            {fld(
              T('Lønnsart'),
              <span>{(ptQ.data ?? []).find((p) => p.id === ruleDraft.payroll_type_id)?.name}</span>,
            )}
            {fld(
              T('Dag'),
              <select
                className="inp"
                aria-label={T('Dag')}
                value={ruleDraft.day ?? 1}
                onChange={(e) => setRuleDraft({ ...ruleDraft, day: Number(e.target.value) })}
              >
                {[1, 2, 3, 4, 5, 6, 7].map((d) => (
                  <option key={d} value={d}>
                    {DN[(d + 6) % 7]}
                  </option>
                ))}
              </select>,
            )}
            {fld(
              T('Tid'),
              <div className="two">
                <input
                  className="inp"
                  type="time"
                  aria-label={T('Fra')}
                  value={ruleDraft.time_from ?? ''}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, time_from: e.target.value })}
                />
                <input
                  className="inp"
                  type="time"
                  aria-label={T('Til')}
                  value={ruleDraft.time_to ?? ''}
                  onChange={(e) => setRuleDraft({ ...ruleDraft, time_to: e.target.value })}
                />
              </div>,
            )}
            {fld(
              T('Terskel, timer per uke'),
              <input
                className="inp"
                type="number"
                aria-label={T('Terskel, timer per uke')}
                style={{ width: 90 }}
                value={ruleDraft.threshold ?? ''}
                onChange={(e) =>
                  setRuleDraft({
                    ...ruleDraft,
                    threshold: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />,
            )}
            {/* Prioritet is gone: the engine never read it, every matching rule counts (M8) */}
            {fld(
              T('Beskrivelse'),
              <input
                className="inp w"
                aria-label={T('Beskrivelse')}
                value={ruleDraft.description ?? ''}
                onChange={(e) => setRuleDraft({ ...ruleDraft, description: e.target.value })}
              />,
            )}
            <p className="hint">
              {T(
                'En regel med terskel gjelder timer over terskelen i uken; ellers gjelder den tidsrommet på dagen. Tider som slutter før start går over midnatt.',
              )}
            </p>
          </>
        )}
      </Drawer>
      <Drawer
        open={!!typeEdit}
        onClose={() => setTypeEdit(null)}
        title={typeEdit?.id ? T('Endre sats') : T('Ny lønnsart')}
        footer={
          <>
            <span className="sp" />
            <Button onClick={() => setTypeEdit(null)}>{T('Avbryt')}</Button>
            <Button
              tone="pri"
              onClick={() => {
                if (!typeEdit?.name || !typeEdit.code) return toast(T('Navn mangler'));
                const body = {
                  name: typeEdit.name,
                  code: typeEdit.code,
                  calculation_type: (typeEdit.rateType === 'percent' ? 'percent' : 'extra_per_hour') as
                    | 'percent'
                    | 'extra_per_hour',
                  rate_value: Number(typeEdit.value ?? 0),
                  yields_to_payroll_type_id: typeEdit.yieldsTo ?? null,
                  pays_whole_hour: typeEdit.paysWholeHour === true,
                };
                (typeEdit.id
                  ? pw.updateType.mutateAsync({ id: typeEdit.id, body })
                  : pw.createType.mutateAsync(body)
                ).then(
                  () => {
                    toast(T('Lønnsarten er lagret'));
                    setTypeEdit(null);
                  },
                  (e) => toast(errorText(e)),
                );
              }}
            >
              {T('Lagre')}
            </Button>
          </>
        }
      >
        {typeEdit && (
          <>
            {fld(
              T('Navn'),
              <input
                className="inp w"
                aria-label={T('Navn')}
                value={typeEdit.name ?? ''}
                onChange={(e) => setTypeEdit({ ...typeEdit, name: e.target.value })}
              />,
            )}
            {fld(
              T('Kode'),
              <input
                className="inp"
                aria-label={T('Kode')}
                style={{ width: 120 }}
                value={typeEdit.code ?? ''}
                onChange={(e) => setTypeEdit({ ...typeEdit, code: e.target.value })}
              />,
            )}
            {fld(
              T('Beregning'),
              <select
                className="inp"
                aria-label={T('Beregning')}
                value={typeEdit.rateType ?? 'extra_per_hour'}
                onChange={(e) => setTypeEdit({ ...typeEdit, rateType: e.target.value })}
              >
                <option value="extra_per_hour">{T('Kroner per time')}</option>
                <option value="percent">{T('Prosent')}</option>
              </select>,
            )}
            {fld(
              T('Sats'),
              <input
                className="inp"
                type="number"
                step="0.01"
                aria-label={T('Sats')}
                style={{ width: 120 }}
                value={typeEdit.value ?? ''}
                onChange={(e) => setTypeEdit({ ...typeEdit, value: e.target.value })}
              />,
            )}
            {/* #143: Helg that yields to Natt pays a weekend night hour the night tillegg only */}
            {fld(
              T('Betales ikke i timer med'),
              <select
                className="inp"
                aria-label={T('Betales ikke i timer med')}
                value={typeEdit.yieldsTo ?? ''}
                onChange={(e) =>
                  setTypeEdit({ ...typeEdit, yieldsTo: e.target.value ? Number(e.target.value) : null })
                }
              >
                <option value="">{T('Ingen, betales sammen med andre tillegg')}</option>
                {(ptQ.data ?? [])
                  .filter((x) => x.id !== typeEdit.id)
                  .map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
              </select>,
            )}
            <p className="hint">
              {T(
                'En time som også har den valgte lønnsarten, får bare den. Eksempel: Helg satt til Natt gir en lørdagsnatt bare nattillegg.',
              )}
            </p>
            {/* #183: PowerOffice's «Overtid 50%» is 150 %: the code pays base and supplement in one line */}
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14 }}>
              <input
                type="checkbox"
                checked={typeEdit.paysWholeHour === true}
                onChange={(e) => setTypeEdit({ ...typeEdit, paysWholeHour: e.target.checked })}
                style={{ marginTop: 3 }}
              />
              <span>
                {T('Lønnsarten betaler hele timen')}
                <span className="hint" style={{ display: 'block' }}>
                  {T(
                    'Kryss av når lønnsarten i lønnssystemet betaler grunnlønn og tillegg sammen, som «Overtid 50 %» med 150 % i PowerOffice. Timene flyttes da fra Vanlige timer til denne linjen, med grunnlønn pluss tillegget over. Satsen her er fortsatt bare tillegget, 50.',
                  )}
                </span>
              </span>
            </label>
          </>
        )}
      </Drawer>
      <Drawer
        open={!!typeDraft}
        onClose={() => setTypeDraft(null)}
        title={typeDraft?.id ? T('Endre vakttype') : T('Ny vakttype')}
        footer={
          <>
            {typeDraft?.id && typeDraft.id !== 1 && (
              <Button
                ghost
                tone="dgr"
                onClick={() =>
                  stw.remove.mutateAsync(typeDraft.id as number).then(
                    () => {
                      toast(T('Slettet'));
                      setTypeDraft(null);
                    },
                    (e) => toast(`${T('Kunne ikke slette')}: ${(e as Error).message}`),
                  )
                }
              >
                {T('Slett')}
              </Button>
            )}
            <span className="sp" />
            <Button onClick={() => setTypeDraft(null)}>{T('Avbryt')}</Button>
            <Button tone="pri" onClick={saveType}>
              {T('Lagre')}
            </Button>
          </>
        }
      >
        {typeDraft && (
          <>
            {fld(
              T('Navn'),
              <input
                className="inp w"
                aria-label={T('Navn')}
                value={typeDraft.name ?? ''}
                onChange={(e) => setTypeDraft({ ...typeDraft, name: e.target.value })}
              />,
            )}
            {fld(
              T('Farge'),
              <input
                className="inp"
                type="color"
                aria-label={T('Farge')}
                value={
                  typeDraft.color_code && /^#[0-9a-f]{6}$/i.test(typeDraft.color_code)
                    ? typeDraft.color_code
                    : '#2f5fc4'
                }
                onChange={(e) => setTypeDraft({ ...typeDraft, color_code: e.target.value })}
              />,
            )}
            {fld(
              T('Tid'),
              <div className="two">
                <input
                  className="inp"
                  type="time"
                  aria-label={T('Fra')}
                  value={typeDraft.from}
                  onChange={(e) => setTypeDraft({ ...typeDraft, from: e.target.value })}
                />
                <input
                  className="inp"
                  type="time"
                  aria-label={T('Til')}
                  value={typeDraft.to}
                  onChange={(e) => setTypeDraft({ ...typeDraft, to: e.target.value })}
                />
              </div>,
            )}
            <p className="hint">{T('Tidene er veggklokke; et sluttidspunkt før start betyr neste dag.')}</p>
          </>
        )}
      </Drawer>
      <Drawer
        open={!!absDraft}
        onClose={() => setAbsDraft(null)}
        title={absDraft?.id ? T('Endre fraværstype') : T('Egendefinert fraværstype')}
        footer={
          <>
            <span className="sp" />
            <Button onClick={() => setAbsDraft(null)}>{T('Avbryt')}</Button>
            <Button tone="pri" onClick={saveAbs}>
              {T('Lagre')}
            </Button>
          </>
        }
      >
        {absDraft && (
          <>
            {fld(
              T('Navn'),
              <input
                className="inp w"
                aria-label={T('Navn')}
                value={absDraft.name ?? ''}
                onChange={(e) => setAbsDraft({ ...absDraft, name: e.target.value })}
              />,
            )}
            {fld(
              T('Kategori'),
              <select
                className="inp"
                aria-label={T('Kategori')}
                value={absDraft.category ?? 'annet'}
                onChange={(e) => setAbsDraft({ ...absDraft, category: e.target.value })}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>
                    {T(c.label)}
                  </option>
                ))}
              </select>,
            )}
            <p className="hint">
              {T('Kategorien bestemmer farge, filter og om fraværet teller som sykefravær i statistikken.')}
            </p>
            {fld(
              T('Farge'),
              <input
                className="inp"
                type="color"
                aria-label={T('Farge')}
                value={
                  absDraft.color_code && /^#[0-9a-f]{6}$/i.test(absDraft.color_code)
                    ? absDraft.color_code
                    : '#586c85'
                }
                onChange={(e) => setAbsDraft({ ...absDraft, color_code: e.target.value })}
              />,
            )}
            {absDraft.is_builtin && (
              <p className="hint">
                {T('Innebygd type')} <span className="mono">{absDraft.key}</span>
                {lawNote(absDraft as ApiAbsenceType) ? `: ${lawNote(absDraft as ApiAbsenceType)}` : ''}.{' '}
                {T('Den kan slås av, men ikke slettes, fordi fravær peker på den.')}
              </p>
            )}
            {fld(
              T('Enhet'),
              absDraft.id ? (
                <span>
                  {unitText(absDraft as ApiAbsenceType, tenant?.vacation_day_basis ?? 'virkedager')}
                </span>
              ) : (
                <select
                  className="inp"
                  aria-label={T('Enhet')}
                  value={absDraft.type ?? 'date'}
                  onChange={(e) => setAbsDraft({ ...absDraft, type: e.target.value })}
                >
                  <option value="date">{T('Hel dag')}</option>
                  <option value="time">{T('Del av dag')}</option>
                  <option value="datetime">{T('Fra–til, med klokkeslett')}</option>
                </select>
              ),
            )}
            {fld(
              T('Dagtelling'),
              <select
                className="inp"
                aria-label={T('Dagtelling')}
                value={absDraft.day_basis ?? ''}
                onChange={(e) =>
                  setAbsDraft({
                    ...absDraft,
                    day_basis: (e.target.value || null) as ApiAbsenceType['day_basis'],
                  })
                }
              >
                <option value="">{T('Ikke angitt')}</option>
                <option value="virkedager">{T('Virkedager (man–lør)')}</option>
                <option value="kalenderdager">{T('Kalenderdager')}</option>
                <option value="arbeidsdager">{T('Arbeidsdager (man–fre)')}</option>
              </select>,
            )}
            <p className="hint">
              {T(
                'Dagtellingen brukes i grensevarslene; virkedager og arbeidsdager hopper over helligdager. En type som trekker feriedager, telles slik bedriften har valgt under Feriedager.',
              )}
            </p>
            {fld(
              T('Hvem betaler'),
              <select
                className="inp"
                aria-label={T('Hvem betaler')}
                value={absDraft.payer ?? ''}
                onChange={(e) =>
                  setAbsDraft({ ...absDraft, payer: (e.target.value || null) as ApiAbsenceType['payer'] })
                }
              >
                <option value="">{T('Etter avtale')}</option>
                {Object.entries(PAYER).map(([k, v]) => (
                  <option key={k} value={k}>
                    {T(v)}
                  </option>
                ))}
              </select>,
            )}
            {fld(
              T('Betalt i lønnsfilen'),
              sw(!!absDraft.is_paid, T('Ja'), T('Nei'), () =>
                setAbsDraft({ ...absDraft, is_paid: !absDraft.is_paid }),
              ),
            )}
            {fld(
              T('Trekker feriedager'),
              sw(!!absDraft.is_deductible, T('Ja'), T('Nei'), () =>
                setAbsDraft({ ...absDraft, is_deductible: !absDraft.is_deductible }),
              ),
            )}
            {fld(
              T('Sykefravær'),
              sw(!!absDraft.is_sick_day, T('Ja, telles i sykefraværet'), T('Nei'), () =>
                setAbsDraft({ ...absDraft, is_sick_day: !absDraft.is_sick_day }),
              ),
            )}
            {fld(
              T('Aktiv'),
              sw(absDraft.is_active !== false, T('Ja'), T('Nei, skjult'), () =>
                setAbsDraft({ ...absDraft, is_active: absDraft.is_active === false }),
              ),
            )}
            {fld(
              T('Maks ganger'),
              <input
                className="inp"
                type="number"
                aria-label={T('Maks ganger')}
                style={{ width: 90 }}
                value={absDraft.max_times ?? ''}
                onChange={(e) =>
                  setAbsDraft({
                    ...absDraft,
                    max_times: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />,
            )}
            {fld(
              T('Maks dager per gang'),
              <input
                className="inp"
                type="number"
                aria-label={T('Maks dager per gang')}
                style={{ width: 90 }}
                value={absDraft.max_days_per_use ?? ''}
                onChange={(e) =>
                  setAbsDraft({
                    ...absDraft,
                    max_days_per_use: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />,
            )}
            {fld(
              T('Periode'),
              <select
                className="inp"
                aria-label={T('Periode')}
                value={absDraft.limit_period ?? 'calendar_year'}
                onChange={(e) => setAbsDraft({ ...absDraft, limit_period: e.target.value })}
              >
                <option value="calendar_year">{T('Kalenderår')}</option>
                <option value="rolling_year">{T('Rullerende 12 måneder')}</option>
              </select>,
            )}
            {fld(
              T('Arbeidsgiver betaler'),
              <>
                <input
                  className="inp"
                  type="number"
                  aria-label={T('Arbeidsgiver betaler')}
                  style={{ width: 90 }}
                  value={absDraft.employer_paid_days ?? ''}
                  onChange={(e) =>
                    setAbsDraft({
                      ...absDraft,
                      employer_paid_days: e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />{' '}
                {T('dager')}
              </>,
            )}
            {fld(
              T('Maks dager per periode'),
              <input
                className="inp"
                type="number"
                min={0}
                aria-label={T('Maks dager per periode')}
                style={{ width: 90 }}
                value={absDraft.max_days_per_period ?? ''}
                onChange={(e) =>
                  setAbsDraft({
                    ...absDraft,
                    max_days_per_period: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />,
            )}
            {fld(
              T('Søknadsfrist, bedriftens (dager)'),
              <input
                className="inp"
                type="number"
                min={0}
                aria-label={T('Søknadsfrist, bedriftens (dager)')}
                style={{ width: 90 }}
                value={absDraft.notice_days ?? ''}
                onChange={(e) =>
                  setAbsDraft({
                    ...absDraft,
                    notice_days: e.target.value === '' ? null : Number(e.target.value),
                  })
                }
              />,
            )}
            <p className="hint">
              {T(
                'Grensene varsler lederen; de stopper ikke registrering eller godkjenning. 0 eller tomt er ingen grense.',
              )}
            </p>
            {fld(
              T('A-melding'),
              <select
                className="inp"
                aria-label={T('A-melding')}
                value={absDraft.a_melding ?? ''}
                onChange={(e) => setAbsDraft({ ...absDraft, a_melding: e.target.value || null })}
              >
                <option value="">{T('Nei, som lønnsart')}</option>
                {A_MELDING.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>,
            )}
            {fld(
              T('Lønnsart'),
              <input
                className="inp"
                aria-label={T('Lønnsart')}
                style={{ width: 120 }}
                value={absDraft.salary_type_code ?? ''}
                onChange={(e) => setAbsDraft({ ...absDraft, salary_type_code: e.target.value || null })}
              />,
            )}
            <p className="hint">{T('Uten lønnsart kommer fraværet ikke med i eksporten.')}</p>
          </>
        )}
      </Drawer>
    </div>
  );
}
